import { type Customer } from '@hungryroot/customer';
import { ButtonsGroup } from '@hungryroot/pho';
import { Paragraph, Heading } from '@hungryroot/ramen';
import { type LikeDislikeTagsRatings } from '@hungryroot/rest';
import { type TagGroups, useCustomerTagGroup } from '@hungryroot/tags';
import {
  usePatchCustomerTagSelf,
  type TagPayload,
} from '../../../../api/v2/customers/[id]/tags/patch/patch-customer-tags';
import {
  useTagGroup,
  type Tag,
} from '../../../../api/v2/tag_groups/get/v2-tag-groups-get';
import styles from './ControlTagTernary.module.css';
interface Props {
  testid?: string;
  customerId: Customer['id'];
  tagGroupId: TagGroups;
  isTwoColumnRow?: boolean;
}

interface CustomerTag {
  customer_id: number;
  preference: LikeDislikeTagsRatings;
  tag: number;
}
interface RowProps {
  tagId: Tag['id'];
  imgSrc: Tag['image_url'];
  tagName: Tag['name'];
  remark: Tag['remark'];
  customerTags: CustomerTag[];
  tagCyid: string;
  onClick: (value: TagPreference) => void;
}

export enum TagPreference {
  Absolutely = 1,
  SoSo = 0,
  NoThanks = -1,
}

const buttonGroupOptions = [
  {
    label: 'Absolutely',
    value: TagPreference.Absolutely,
  },
  {
    label: 'So-so',
    value: TagPreference.SoSo,
  },
  {
    label: 'No thanks',
    value: TagPreference.NoThanks,
  },
];

function getTagPreference(
  tagId: number,
  array: TagPayload[]
): TagPayload['preference'] | undefined {
  const match = array.find(({ tag }) => {
    return tag === tagId;
  });
  return match && match.preference;
}

const TwoColumnRow = ({
  tagId,
  imgSrc,
  tagName,
  remark,
  customerTags,
  tagCyid,
  onClick,
}: RowProps) => {
  return (
    <div key={tagId} className={styles.column}>
      <img
        src={imgSrc ? imgSrc : `/public/img/quiz/placeholder-image.png`}
        alt={tagName}
        className={styles.image}
      />
      <div className={styles.preferences}>
        <div className={styles.header}>
          <Heading variant="H6">{tagName}</Heading>
          {remark && (
            <Paragraph className={styles.description}>{remark}</Paragraph>
          )}
        </div>
        <ButtonsGroup
          buttonClassName={styles['button']}
          testid={tagCyid}
          selected={getTagPreference(tagId, customerTags)}
          handleClick={onClick}
          options={buttonGroupOptions}
        />
      </div>
    </div>
  );
};

const ThreeColumnRow = ({
  tagId,
  imgSrc,
  tagName,
  remark,
  customerTags,
  tagCyid,
  onClick,
}: RowProps) => {
  return (
    <div key={tagId} className={styles.row}>
      <img
        src={imgSrc ? imgSrc : `/public/img/quiz/placeholder-image.png`}
        alt={tagName}
        className={styles.image}
      />
      <div className={styles.controls}>
        <div>
          <Heading variant="H6">{tagName}</Heading>
          {remark && (
            <Paragraph className={styles.description}>{remark}</Paragraph>
          )}
        </div>
        <div className={styles['button-group']}>
          <ButtonsGroup
            testid={tagCyid}
            selected={getTagPreference(tagId, customerTags)}
            handleClick={onClick}
            options={buttonGroupOptions}
          />
        </div>
      </div>
    </div>
  );
};

export function ControlTagTernary(props: Props) {
  const { testid, customerId, tagGroupId, isTwoColumnRow } = props;

  const usePatchCustomerTagSelfMutation = usePatchCustomerTagSelf();
  const tagGroup = useTagGroup(tagGroupId);
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;

  const { data: customerTagGroupData } = useCustomerTagGroup(tagGroupId);
  const customerTags =
    customerTagGroupData?.results.map((obj) => {
      return {
        customer_id: customerId,
        preference: obj.preference,
        tag: obj.tag,
      };
    }) || [];

  function handleChange(tag: Tag, preference: TagPreference) {
    const payload = {
      customer_id: customerId,
      tag: tag.id,
      preference,
    };

    return usePatchCustomerTagSelfMutation.mutate({ data: [payload] });
  }

  return (
    <>
      {queryTags?.map((tag) => {
        const { id: tagId, image_url: imgSrc, name: tagName, remark } = tag;
        const tagCyid = `${testid}-toggle-tag-${tagId}`;

        const props = {
          tagId,
          imgSrc,
          tagName,
          remark,
          customerTags,
          tagCyid,
          onClick: (value: TagPreference) => {
            handleChange(tag, value);
          },
        };

        if (isTwoColumnRow) {
          return <TwoColumnRow key={tagId} {...props} />;
        } else {
          return <ThreeColumnRow key={tagId} {...props} />;
        }
      })}
    </>
  );
}
